/**
 * Invoice Service
 * Handles all API calls related to invoice management
 */

import { apiClient } from './apiClient';
import { getInvoiceEndpoint } from '@/config/apiConfig';
import { configService } from './configService';
import {
  Invoice,
  InvoiceListParams,
  InvoiceListResponse,
  InvoiceUpdateRequest,
  FinanceInvoiceUpdateRequest,
  InvoiceLockRequest,
  InvoiceUpdateResponse,
  HonorariumCalculation,
  HonorariumResult,
  BulkSalaryImportData,
  BulkImportResult,
  InvoiceSection
} from '@/types/invoice';

class InvoiceService {
  // Configuration data accessed through configService
  get GRANT_TYPES() {
    return configService.getGrantTypes();
  }

  get DONOR_OPTIONS() {
    return configService.getDonors();
  }

  get BUDGET_OPTIONS() {
    return configService.getBudgetIds();
  }

  get DEPARTMENTS() {
    return configService.getDepartments();
  }

  // Convenience getters for backward compatibility
  readonly GRANT_TYPE_VALUES = {
    DOMESTIC: 'Domestic Grant',
    FOREIGN: 'Foreign Grant'
  } as const;
  /**
   * Get list of invoices with optional filtering and pagination
   * @param section - The section (pnc, bu, finance) for role-based access
   * @param params - Query parameters for filtering and pagination
   */
  async getInvoices(section: InvoiceSection, params: InvoiceListParams = {}): Promise<InvoiceListResponse> {
    try {
      const endpoint = getInvoiceEndpoint(section, 'list');
      const finalParams = {
        ...params,
        // Ensure page_size has a minimum value
        page_size: params.page_size && params.page_size >= 10 ? params.page_size : 10,
      };
      
      const response = await apiClient.get<InvoiceListResponse>(endpoint, {
        params: finalParams
      });
      return response.data;
    } catch (error) {
      console.error(`[Invoice Service] Error fetching invoices for section ${section}:`, error);
      throw error;
    }
  }

  /**
   * Update single or multiple invoices for a specific section
   * @param section - The section (pnc, bu, or finance) performing the update
   * @param invoices - Invoice update request(s)
   */
  async updateInvoices(
    section: 'pnc' | 'bu' | 'finance',
    invoices: InvoiceUpdateRequest | InvoiceUpdateRequest[] | FinanceInvoiceUpdateRequest | FinanceInvoiceUpdateRequest[]
  ): Promise<InvoiceUpdateResponse> {
    try {
      const endpoint = getInvoiceEndpoint(section, 'update');
      const response = await apiClient.patch<InvoiceUpdateResponse>(endpoint, invoices);
      
      return response.data;
    } catch (error) {
      console.error(`[Invoice Service] Error updating invoices for section ${section}:`, error);
      throw error;
    }
  }

  /**
   * Update a single invoice for a specific section
   * @param section - The section (pnc, bu, or finance) performing the update
   * @param invoice - Invoice update request
   */
  async updateInvoice(
    section: 'pnc' | 'bu' | 'finance', 
    invoice: InvoiceUpdateRequest | FinanceInvoiceUpdateRequest
  ): Promise<InvoiceUpdateResponse> {
    return this.updateInvoices(section, invoice);
  }

  /**
   * Lock/approve single or multiple invoices for a specific section
   * @param section - The section (pnc or bu) performing the lock
   * @param invoices - Invoice lock request(s)
   */
  async lockInvoices(
    section: 'pnc' | 'bu',
    invoices: InvoiceLockRequest | InvoiceLockRequest[]
  ): Promise<InvoiceUpdateResponse> {
    try {
      const endpoint = getInvoiceEndpoint(section, 'lock');
      const response = await apiClient.patch<InvoiceUpdateResponse>(endpoint, invoices);
      
      return response.data;
    } catch (error) {
      console.error(`[Invoice Service] Error locking invoices for section ${section}:`, error);
      throw error;
    }
  }

  /**
   * Lock a single invoice for a specific section
   * @param section - The section (pnc or bu) performing the lock
   * @param invoiceId - ID of the invoice to lock
   */
  async lockInvoice(section: 'pnc' | 'bu', invoiceId: number): Promise<InvoiceUpdateResponse> {
    return this.lockInvoices(section, { id: invoiceId });
  }

  /**
   * Generate invoice HTML for viewing
   * @param invoiceId - ID of the invoice to generate
   * @returns HTML content as string
   */
  async generateInvoiceHtml(section: 'finance',invoiceId: number): Promise<string> {
    try {
      const endpoint = getInvoiceEndpoint(section, 'generate')+'/'+invoiceId;
      const response = await apiClient.get(endpoint, {
        responseType: 'text' // Get response as text for HTML content
      });
      return response.data;
    } catch (error) {
      console.error(`[Invoice Service] Error generating invoice HTML for invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  /**
   * Open invoice HTML in a new browser tab
   * @param invoiceId - ID of the invoice to view
   */
  async viewInvoice(section: 'finance',invoiceId: number): Promise<void> {
    try {
      const htmlContent = await this.generateInvoiceHtml(section,invoiceId);
      
      // Create a blob with HTML content and proper MIME type
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(htmlBlob);
      
      // Open HTML in new tab
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay (optional, but good practice)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error(`[Invoice Service] Error viewing invoice ${invoiceId}:`, error);
      throw error;
    }
  }

  /**
   * Calculate honorarium based on attended days, exceptions, and base salary
   */
  calculateHonorarium({
    attended_days,
    exceptions,
    base_salary,
    month_year
  }: HonorariumCalculation): HonorariumResult {
    // Parse month_year to get number of days in that month
    const [year, month] = month_year.split('-').map(Number);
    const daysInMonth = new Date(year, month - 1, 0).getDate();
    
    // Calculate total effective days
    const total_days = attended_days + exceptions;
    
    // Calculate salary using the formula: 
    // (attended_days + exceptions) / Number of days in selected month * base salary
    const calculated_salary = Math.round((total_days / daysInMonth) * base_salary);
    
    const formula_explanation = `(${attended_days} + ${exceptions}) / ${daysInMonth} * ₹${base_salary} = ₹${calculated_salary}`;
    
    return {
      total_days,
      days_in_month: daysInMonth,
      calculated_salary,
      formula_explanation
    };
  }

  /**
   * Process bulk honorarium import from CSV/Excel data
   */
  async processBulkSalaryImport(
    data: BulkSalaryImportData[],
    targetInvoices: Invoice[]
  ): Promise<BulkImportResult> {
    const result: BulkImportResult = {
      successful: 0,
      failed: 0,
      errors: []
    };

    try {
      const updatePromises = data.map(async (item, index) => {
        try {
          // Find matching invoice by employee_id and month_year
          const targetInvoice = targetInvoices.find(
            inv => inv.employee_id === item.employee_id && inv.month_year === item.month_year
          );

          if (!targetInvoice) {
            result.errors.push({
              row: index + 1,
              employee_id: item.employee_id,
              error: `No invoice found for associate ${item.employee_id} in ${item.month_year}`
            });
            result.failed++;
            return;
          }

          if (targetInvoice.state === 'Approved By BU') {
            result.errors.push({
              row: index + 1,
              employee_id: item.employee_id,
              error: 'Invoice is already approved by BU and cannot be updated'
            });
            result.failed++;
            return;
          }

          // Update the invoice with base honorarium (assuming PnC section for bulk import)
          await this.updateInvoice('pnc', {
            id: targetInvoice.id,
            invoice_value: item.base_salary // Updated field name
          });

          result.successful++;
        } catch (error) {
          result.errors.push({
            row: index + 1,
            employee_id: item.employee_id,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          result.failed++;
        }
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('[Invoice Service] Error processing bulk import:', error);
      throw error;
    }

    return result;
  }

  /**
   * Export invoices to CSV format (general purpose)
   */
  exportToCSV(invoices: Invoice[]): string {
    const headers = [
      'Employee ID',
      'Name',
      'Email', 
      'Program',
      'Designation',
      'Month',
      'Attended Days',
      'Exceptions',
      'Invoice Value',
      'Calculated Amount',
      'PnC Remarks',
      'BU Remarks',
      'State'
    ];

    const csvData = invoices.map(invoice => [
      invoice.employee_id,
      invoice.name,
      invoice.email,
      invoice.program,
      invoice.designation,
      invoice.month_year,
      invoice.attended_days,
      invoice.exceptions || 0,
      invoice.invoice_value || '',
      this.calculateHonorarium({
        attended_days: invoice.attended_days,
        exceptions: invoice.exceptions || 0,
        base_salary: invoice.invoice_value || 0,
        month_year: invoice.month_year
      }).calculated_salary,
      invoice.pnc_remarks || '',
      invoice.bu_remarks || '',
      invoice.state || 'Draft'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n');

    return csvContent;
  }

  /**
   * Export invoices to CSV format specifically for Finance software import
   */
  exportToFinanceCSV(invoices: Invoice[]): string {
    const headers = [
      'Bill Date',
      'Bill No',
      'RG ID',
      'Vendor Name',
      'Memo',
      'Budget ID',
      'Department ID',
      'Type of Grant',
      'Donor Name',
      'Vendor Bill Amount'
    ];

    const csvData = invoices.map(invoice => {
      const calculatedAmount = this.calculateHonorarium({
        attended_days: invoice.attended_days,
        exceptions: invoice.exceptions || 0,
        base_salary: invoice.invoice_value || 0,
        month_year: invoice.month_year
      }).calculated_salary;

      const donorName = configService.getDonorNameById(invoice.donor_id);
      const departmentName = configService.getDepartmentNameById(invoice.department_id);
      const billDate = invoice.bu_approved_at 
        ? new Date(invoice.bu_approved_at).toLocaleDateString('en-GB')
        : new Date().toLocaleDateString('en-GB');
      
      return [
        billDate,
        `${invoice.employee_id}-${invoice.month_year}`,
        invoice.employee_id,
        invoice.name,
        `${invoice.program} - ${invoice.designation} - ${invoice.month_year} (${invoice.attended_days + (invoice.exceptions || 0)} days)`,
        invoice.budget_id || '',
        departmentName,
        invoice.grant_type || '',
        donorName,
        calculatedAmount
      ];
    });

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n');

    return csvContent;
  }

  /**
   * Download CSV file (general purpose)
   */
  downloadCSV(invoices: Invoice[], filename: string = 'invoices-export.csv'): void {
    const csvContent = this.exportToCSV(invoices);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Export invoices to CSV using API endpoint
   */
  async exportInvoices(section: 'pnc' | 'bu' | 'finance', filters?: Partial<InvoiceListParams>, filename?: string): Promise<void> {
    try {
      const endpoint = getInvoiceEndpoint(section, 'export');
      
      // Remove pagination parameters for export
      const { page, page_size, ...exportFilters } = filters || {};
      
      const response = await apiClient.get(endpoint, {
        params: exportFilters,
        responseType: 'blob',
        timeout: 120000,
        _skipRetry: true,
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');

      const defaultFilename = filename || `${section}-invoices-${new Date().toISOString().split('T')[0]}.csv`;
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', defaultFilename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error(`[Invoice Service] Error exporting ${section} invoices:`, error);
      throw error;
    }
  }

  /**
   * Download CSV file specifically formatted for Finance software import using API
   */
  async downloadFinanceCSV(filters?: InvoiceListParams, filename: string = 'finance-import.csv'): Promise<void> {
    try {
      const endpoint = getInvoiceEndpoint('finance', 'export');
      
      // Remove pagination parameters for export
      const { page, page_size, ...exportFilters } = filters || {};
      
      const params = {
        ...exportFilters,
        finance_format: true
      };
      
      const response = await apiClient.get(endpoint, {
        params,
        responseType: 'blob',
        timeout: 120000,
        _skipRetry: true,
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('[Invoice Service] Error downloading finance CSV:', error);
      throw error;
    }
  }

  /**
   * Download CSV file specifically formatted for Finance software import (legacy client-side method)
   */
  downloadFinanceCSVLegacy(invoices: Invoice[], filename: string = 'finance-import.csv'): void {
    const csvContent = this.exportToFinanceCSV(invoices);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Parse CSV file for bulk import
   */
  parseCSV(csvText: string): BulkSalaryImportData[] {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must contain headers and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const employeeIdIndex = headers.findIndex(h => (h.includes('employee') || h.includes('associate')) && h.includes('id'));
    const salaryIndex = headers.findIndex(h => h.includes('salary') || h.includes('base') || h.includes('honorarium'));
    const monthIndex = headers.findIndex(h => h.includes('month') || h.includes('year'));

    if (employeeIdIndex === -1 || salaryIndex === -1 || monthIndex === -1) {
      throw new Error('CSV must contain Associate ID, Base Honorarium, and Month Year columns');
    }

    const data: BulkSalaryImportData[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
      
      if (values.length > Math.max(employeeIdIndex, salaryIndex, monthIndex)) {
        const employee_id = values[employeeIdIndex];
        const base_salary = parseFloat(values[salaryIndex]);
        const month_year = values[monthIndex];

        if (employee_id && !isNaN(base_salary) && month_year) {
          data.push({
            employee_id,
            base_salary,
            month_year
          });
        }
      }
    }

    return data;
  }
}

// Create and export singleton instance
export const invoiceService = new InvoiceService();
export default invoiceService;